import React, { useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { useModal } from '../providers/ModalContext';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md' 
}) => {
  const { closeAllModals } = useModal();
  
  const sizeClasses = {
    sm: 'w-11/12 sm:w-96 max-w-md',
    md: 'w-11/12 sm:w-96 max-w-lg',
    lg: 'w-11/12 sm:w-2/3 max-w-4xl',
    xl: 'w-11/12 sm:w-4/5 max-w-6xl'
  };

  const handleClose = useCallback(() => {
    onClose();
    closeAllModals();
  }, [onClose, closeAllModals]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
      <div className={`modal-content relative bg-white rounded-xl shadow-2xl border border-gray-100 my-8 ${sizeClasses[size]} mx-auto`}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-white hover:bg-opacity-80 rounded-full p-2 transition-all duration-200 transform hover:scale-105"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
